import { ChangeEvent, useCallback } from 'react';
import { Button, Select, TextInput } from 'ui';
import { TrashIcon } from 'ui/icons';
import { MeetingReminder, MeetingReminderUnit } from 'compliance-api';

const reminderUnitOptions = [
  { value: MeetingReminderUnit.Minutes, label: 'Minutes' },
  { value: MeetingReminderUnit.Hours, label: 'Hours' },
  { value: MeetingReminderUnit.Weeks, label: 'Weeks' },
  { value: MeetingReminderUnit.Months, label: 'Months' },
];

interface MeetingRemindersFieldProps {
  reminders: MeetingReminder[];
  errors?: Array<Partial<Record<keyof MeetingReminder, string>>> | string;
  canAddReminder: boolean;
  onAdd: () => void;
  onChange: (index: number, value: Partial<MeetingReminder>) => void;
  onDelete: (index: number) => void;
}

export const MeetingRemindersField = ({
  reminders,
  errors,
  canAddReminder,
  onAdd,
  onChange,
  onDelete,
}: MeetingRemindersFieldProps) => {
  const getReminderErrors = useCallback(
    (index: number) => {
      return Array.isArray(errors) ? errors[index] : undefined;
    },
    [errors],
  );

  return (
    <div className="flex flex-col gap-4">
      {reminders.map((reminder, index) => {
        const reminderErrors = getReminderErrors(index);

        return (
          <div key={index} className="grid grid-cols-[minmax(0,_1fr)_minmax(0,_1fr)_auto] gap-4">
            <TextInput
              name={`reminders.${index}.value`}
              label="Reminder before"
              type="number"
              value={reminder.value ?? ''}
              min={1}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                onChange(index, {
                  value: event.target.value === '' ? undefined : Number(event.target.value),
                });
              }}
              error={reminderErrors?.value}
              className="min-w-0 w-full"
            />
            <Select
              label="Unit"
              options={reminderUnitOptions}
              value={reminder.unit}
              onChange={unit => onChange(index, { unit })}
              isClearable={false}
              error={reminderErrors?.unit}
            />
            <button
              type="button"
              onClick={() => onDelete(index)}
              className="self-end pb-3"
              aria-label="Delete reminder"
            >
              <img src={TrashIcon} alt="Delete reminder" />
            </button>
          </div>
        );
      })}

      <Button
        type="button"
        variant="plain"
        className="w-fit !px-0"
        onClick={onAdd}
        disabled={!canAddReminder}
      >
        Add new reminder
      </Button>
    </div>
  );
};
